import { inferUnifiedRole } from '@/lib/folderRoles';
import type { Folder } from '@/types/email';

/**
 * Resolve the target folder path for a specific account.
 * If the target folder belongs to this account, use its path directly.
 * Otherwise, find the equivalent folder in this account by role, path, or name.
 * Returns null if no match is found.
 */
export function resolveTargetFolderForAccount(
  targetFolder: Folder,
  accountId: string,
  accountFolderCache: Record<string, Folder[]>,
): string | null {
  // Direct match — folder belongs to this account
  if (targetFolder.accountId === accountId) {
    return targetFolder.path;
  }

  const accountFolders = accountFolderCache[accountId] ?? [];

  // Match by unified role first (most reliable for system folders)
  const targetRole = inferUnifiedRole(targetFolder);
  if (targetRole) {
    const byRole = accountFolders.find((f) => inferUnifiedRole(f) === targetRole);
    if (byRole) return byRole.path;
  }

  // Match by path (case-insensitive)
  const byPath = accountFolders.find((f) =>
    f.path.toLowerCase() === targetFolder.path.toLowerCase()
  );
  if (byPath) return byPath.path;

  // Match by name (case-insensitive)
  const byName = accountFolders.find((f) =>
    f.name.toLowerCase() === targetFolder.name.toLowerCase()
  );
  if (byName) return byName.path;

  return null;
}
